import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authApi, socioApi, ApiError } from "@/api/client";
import { keystore } from "@/keystore";
import { PasswordInput } from "@/components/PasswordInput";

/**
 * Primeira ativação da conta. Sócio entra com username pré-cadastrado
 * pelo admin, escolhe senha, e o navegador GERA a chave dona da conta.
 *
 * A chave é SEMPRE mostrada pro usuário no fim (pra backup), e ele escolhe
 * onde guardá-la:
 *  - "system": cifrada com a senha, salva no nosso banco (recupera em
 *    qualquer device só com a senha) — DEFAULT, fácil.
 *  - "browser": só neste navegador (IndexedDB cifrado).
 *  - "self": auto-custódia — não fica no navegador nem no banco; o usuário
 *    guarda a chave por conta própria.
 */

type Custody = "system" | "browser" | "self";

const CUSTODY_OPTIONS: { value: Custody; icon: string; title: string; desc: string }[] = [
  { value: "system", icon: "🏦", title: "Guardar no sistema (recomendado)",
    desc: "Sua chave fica cifrada com sua senha no nosso banco. Você recupera em qualquer aparelho só com login + senha. O sistema nunca vê sua chave." },
  { value: "browser", icon: "💻", title: "Só neste navegador",
    desc: "A chave fica só neste aparelho. Se limpar o navegador ou trocar de aparelho sem backup, perde o acesso." },
  { value: "self", icon: "🔐", title: "Auto-custódia (avançado)",
    desc: "Não guardamos sua chave em lugar nenhum. Você é 100% responsável pelo backup. Máximo controle, máximo risco." },
];

export default function Setup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState(params.get("u") ?? "");
  const [code, setCode] = useState(params.get("codigo") ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [custody, setCustody] = useState<Custody>("system");
  const [step, setStep] = useState<"input" | "confirm" | "creating" | "done">("input");
  const [error, setError] = useState<string | null>(null);
  const [createdAddress, setCreatedAddress] = useState<string | null>(null);
  const [privateKey, setPrivateKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const fromInvite = !!params.get("codigo");

  // Validação de username: 3–100 chars, letras/números/._- (igual ao backend).
  // No convite o username é opcional (vazio → backend usa o e-mail).
  const usernameLimpo = username.trim();
  const usernameValido = usernameLimpo === ""
    ? fromInvite                       // vazio só é aceito no fluxo de convite
    : /^[A-Za-z0-9._-]{3,100}$/.test(usernameLimpo);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError("Senhas não conferem");
      return;
    }
    if (password.length < 8) {
      setError("Senha precisa ter pelo menos 8 caracteres");
      return;
    }
    if (!code.trim() && !usernameLimpo) {
      setError("Informe seu usuário, ou use o link/código de convite que você recebeu.");
      return;
    }
    if (usernameLimpo && !usernameValido) {
      setError("Usuário inválido: use 3 a 100 caracteres (letras, números, ponto, hífen ou underscore).");
      return;
    }

    // Username é decisão definitiva → passo de confirmação antes de criar.
    setStep("confirm");
  }

  async function doActivate() {
    setError(null);
    setStep("creating");
    try {
      const { address } = await keystore.create(password);
      // Com código → ativação por convite; o convidado pode escolher um
      // username (opcional — se vazio, o backend mantém o padrão = e-mail).
      // Sem código → ativação manual por username (caso do dono que já existe).
      if (code.trim()) {
        await authApi.activateByCode(code.trim(), password, address, username.trim() || undefined);
      } else {
        await authApi.activate(username, password, address);
      }

      // Guarda a chave em claro só na memória do componente, pra exibir/baixar.
      const priv = keystore.getPrivateKey();

      // Aplica a custódia escolhida.
      if (custody === "system") {
        const vault = await keystore.exportVault();
        if (vault) await socioApi.putKeyVault(vault);
      } else if (custody === "self") {
        // Auto-custódia: não deixa rastro no navegador. O usuário precisa
        // do backup pra usar a conta depois (vai importar quando logar).
        await keystore.destroy();
      }
      // "browser": fica no IndexedDB (já salvo pelo create), nada a fazer.

      setPrivateKey(priv);
      setCreatedAddress(address);
      setStep("done");
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro inesperado");
      // Volta pra confirmação (não pro formulário) pra preservar o que foi
      // digitado e deixar claro qual username falhou (ex: já em uso).
      setStep("confirm");
      await keystore.destroy().catch(() => undefined);
    }
  }

  async function copyKey() {
    if (!privateKey) return;
    await navigator.clipboard.writeText(privateKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadKey() {
    if (!privateKey || !createdAddress) return;
    const content =
      `Goyaz Games — backup da chave\n` +
      `Conta: ${createdAddress}\n` +
      `Chave privada: ${privateKey}\n\n` +
      `GUARDE ESTE ARQUIVO EM LOCAL SEGURO. Quem tem esta chave controla a conta.\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `goyaz-chave-${createdAddress.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (step === "confirm" || step === "creating") {
    const criando = step === "creating";
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="app-card max-w-lg w-full p-6">
          <h1 className="text-2xl font-bold mb-1">Confirme seu usuário</h1>
          <p className="text-sm text-app-muted mb-4">
            Esse é o nome com que você vai entrar na conta. Confira com atenção:
          </p>

          <div className="border border-app-accent/40 bg-app-accent/10 rounded p-4 mb-4">
            <p className="text-xs uppercase tracking-wide text-app-muted mb-1">Seu usuário</p>
            <p className="text-2xl font-bold font-mono break-all">
              {usernameLimpo || <span className="italic font-sans font-normal text-app-muted">seu e-mail do convite</span>}
            </p>
          </div>

          <div className="border border-amber-500/40 bg-amber-500/10 rounded p-3 text-sm mb-4">
            ⚠️ <strong>Atenção:</strong> esse é o login da sua conta on-chain. Anote num lugar
            seguro — se esquecer, você pode perder o acesso à conta e aos seus tokens.
          </div>

          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          <div className="flex gap-2">
            <button type="button" onClick={() => { setError(null); setStep("input"); }}
                    disabled={criando}
                    className="flex-1 bg-app-surface-2 border border-app-border rounded py-2 text-sm font-medium hover:bg-app-border disabled:opacity-50">
              ← Voltar e corrigir
            </button>
            <button type="button" onClick={doActivate} disabled={criando}
                    className="flex-1 bg-app-accent text-app-accent-text rounded py-2 font-medium hover:opacity-90 disabled:opacity-50">
              {criando ? "Gerando chave..." : "Confirmar e ativar"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="app-card max-w-lg w-full p-6">
          <h1 className="text-2xl font-bold mb-1">Conta ativada ✅</h1>
          <p className="text-sm text-app-muted mb-4">
            Endereço da sua conta on-chain:
          </p>
          <pre className="bg-app-surface-2 border border-app-border p-2 rounded text-xs break-all mb-4">{createdAddress}</pre>

          <div className="border border-amber-500/40 bg-amber-500/10 rounded p-4">
            <h2 className="font-semibold text-sm mb-1">🔑 Sua chave privada — faça backup agora</h2>
            <p className="text-xs text-app-muted mb-3">
              {custody === "system"
                ? "Guardamos uma cópia cifrada com sua senha, mas anote também: é a única forma de recuperar se esquecer a senha."
                : custody === "self"
                ? "NÃO guardamos sua chave. Se perder isto, perde a conta pra sempre."
                : "Salvamos só neste navegador. Guarde uma cópia: sem ela, trocar de aparelho = perder acesso."}
            </p>
            <pre className="bg-app-surface border border-app-border p-2 rounded text-xs break-all font-mono select-all">{privateKey}</pre>
            <div className="flex gap-2 mt-2">
              <button onClick={copyKey} type="button"
                      className="flex-1 bg-app-surface-2 border border-app-border rounded py-2 text-sm hover:bg-app-border">
                {copied ? "✓ Copiado" : "Copiar"}
              </button>
              <button onClick={downloadKey} type="button"
                      className="flex-1 bg-app-surface-2 border border-app-border rounded py-2 text-sm hover:bg-app-border">
                Baixar .txt
              </button>
            </div>
          </div>

          <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={savedConfirmed}
                   onChange={(e) => setSavedConfirmed(e.target.checked)}
                   className="mt-1" />
            <span>Guardei minha chave num lugar seguro.</span>
          </label>

          <button onClick={() => navigate("/dashboard")} disabled={!savedConfirmed}
                  className="w-full bg-app-accent text-app-accent-text rounded py-2 mt-4 font-medium hover:opacity-90 disabled:opacity-50">
            Ir pro dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="app-card max-w-lg w-full p-6">
        <h1 className="text-2xl font-bold mb-1">Ativar conta</h1>
        <p className="text-sm text-app-muted mb-4">
          Defina sua senha. O navegador vai gerar sua chave on-chain agora — ela fica
          cifrada com essa senha. <strong>Não esqueça a senha.</strong>
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          {fromInvite ? (
            <>
              <div className="bg-app-accent/10 border border-app-accent/40 rounded p-3 text-sm">
                🎟️ Você foi <strong>convidado</strong>. Código:{" "}
                <code className="font-mono tracking-wider">{code}</code>. Escolha seu usuário e defina sua senha pra ativar.
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Escolha seu usuário <span className="text-app-muted font-normal">(opcional)</span>
                </label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                       className="w-full px-3 py-2" autoComplete="username"
                       placeholder="ex: hiro" />
                <p className="text-xs text-app-muted mt-1">
                  É o nome com que você vai logar. Se deixar em branco, usamos seu e-mail.
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Usuário</label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                       className="w-full px-3 py-2"
                       placeholder="ex: zighue" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Código de convite <span className="text-app-muted font-normal">(se você recebeu um)</span>
                </label>
                <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                       className="w-full px-3 py-2 font-mono tracking-wider"
                       placeholder="ex: K7MNP2QXR4TV" autoComplete="off" />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Senha (≥ 8 chars)</label>
            <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)}
                           minLength={8} autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirmar senha</label>
            <PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                           minLength={8} autoComplete="new-password" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Onde guardar sua chave?</label>
            <div className="space-y-2">
              {CUSTODY_OPTIONS.map((opt) => (
                <label key={opt.value}
                       className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition ${
                         custody === opt.value
                           ? "border-app-accent bg-app-accent/10"
                           : "border-app-border hover:bg-app-surface-2"
                       }`}>
                  <input type="radio" name="custody" value={opt.value}
                         checked={custody === opt.value}
                         onChange={() => setCustody(opt.value)}
                         className="mt-1" />
                  <span>
                    <span className="font-medium text-sm">{opt.icon} {opt.title}</span>
                    <span className="block text-xs text-app-muted mt-0.5">{opt.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit"
                  className="w-full bg-app-accent text-app-accent-text rounded py-2 font-medium disabled:opacity-50 hover:opacity-90">
            Continuar →
          </button>
        </form>
      </div>
    </div>
  );
}
